<template>
  <div class="input-container">
    <input 
      ref="input" 
      v-model="localInput"
      class="wide-input"
      :placeholder="placeholder"
      type="text"
      @blur="handleBlur"
      @focus="showDropdown = true"
    />
    <div 
      :class="{'dropdown-shown' : showDropdown }" 
      class="material-symbols-outlined icon-expand"
      @mousedown.prevent
      @click.stop="toggleFocusInput" 
    >
      expand_more
    </div>
    
    <ul 
      v-if="selectableModules.length && showDropdown" 
      class="suggestion-dropdown" 
      :style="dropdownStyle"
      @mousedown.prevent
    >
      <DropdownNode
        v-for="group in sortedGroups"
        :key="getGroupId(group)"
        :node="group"
        :depth="0"
        :collapsedGroups="collapsedGroups"
        :showName="showName"
        :showLanguage="showLanguage"
        :showECTS="showECTS"
        :unavailableReasonText="unavailableReasonText"
        :unavailableReason="unavailableReason"
        :currentSemester="currentSemester"
        @toggle-group="toggleGroup"
        @select-module="(event, module) => $emit('select-module', event, module)"
        @info-click="module => $emit('info-click', module)"
        @hover-module="hoveredModuleId => $emit('hover-module', hoveredModuleId)"
      />
    </ul>
  </div>
</template>

<script>
import { reactive } from 'vue';
import DropdownNode from './DropdownNode.vue';
import { getExplanatoryUnavailableReason } from '@/utils';

export default {
  name: "ModuleSearchDropdown",
  components: { DropdownNode },
  props: {
    moduleInput: {
      type: String,
      default: ""
    },
    selectableModules: {
      type: Array,
      required: true
    },
    groupedModules: {
      type: Array,
      required: true
    },
    hasStructureGroups: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: "Search modules..."
    },
    unavailableReason: {
      type: String,
      default: "not offered"
    },
    currentSemester: {
      type: [Number, String],
      default: null
    },
    truncateName: {
      type: Boolean,
      default: false
    },
    showLanguage: {
      type: Boolean,
      default: true
    },
    showECTS: {
      type: Boolean,
      default: true
    },
    showName: {
      type: Boolean,
      default: true
    },
    forceCloseDropdown: {
      type: Boolean,
      default: false
    },
    isInfoModalOpen: {
      type: Boolean,
      default: false
    },
    canExitModal: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:moduleInput', 'select-module', 'info-click', 'hover-module'],
  data() {
    return {
      localInput: this.moduleInput,
      showDropdown: false,
      collapsedGroups: reactive({}),
      dropdownMaxHeight: null
    };
  },
  computed: {
    sortedGroups() {
      return this.groupedModules;
    },
    unavailableReasonText() {
        if (this.unavailableReason === "not offered") {
            return "(not offered)";
        }
        return `(${this.unavailableReason})`;
    },
    dropdownStyle() {
      if (this.dropdownMaxHeight) {
        return {
          maxHeight: `${this.dropdownMaxHeight}px`
        };
      }
      return {};
    }
  },
  watch: {
    showDropdown(newVal) {
      if (newVal) {
        this.updateDropdownHeight();
      }
    },
    moduleInput(newVal) {
      if (this.localInput !== newVal) {
        this.localInput = newVal;
      }
    },
    localInput(newVal) {
      this.$emit('update:moduleInput', newVal);
      this.showDropdown = true;
    },
    forceCloseDropdown(newVal, oldVal) {
      if (newVal && !oldVal) {
         this.showDropdown = false;
         if (this.$refs.input) {
            this.$refs.input.blur();
         }
      }
    },
    isInfoModalOpen(newVal, oldVal) {
      if (!newVal && oldVal) {
        // When info modal closes, if we don't have focus, we might want to close or keep open.
        // Usually, keeping it open or restoring focus is preferred, but user might click outside.
        // Let's just focus the input to keep it active.
        if (this.showDropdown && this.$refs.input) {
          this.$refs.input.focus();
        }
      }
    }
  },
  methods: {
    getModuleTooltip(module, isUnavailable) {
      if (!isUnavailable && !module?.isUnavailable) {
        return module.name;
      }
      const startSem = this.$store?.state?.preferences?.startSemester;
      const reasonObj = getExplanatoryUnavailableReason(
        module,
        this.unavailableReason,
        this.currentSemester,
        startSem
      );
      return `${module.name} (${reasonObj.short})`;
    },
    getGroupHeaderText(group) {
      if (group.isUnavailable) {
        return 'Unselectable modules';
      }
      if (!group.structure) {
        return 'All available modules';
      }
      
      let name = group.structure.name || '';
      
      if (group.structure.isExtra || group.structure.isCustomName) {
        return name;
      }
      
      return `Modules part of ${name}`;
    },
    toggleGroup(groupId, event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      
      this.collapsedGroups = {
        ...this.collapsedGroups,
        [groupId]: !this.collapsedGroups[groupId]
      };
    },
    isGroupCollapsed(group) {
      const groupId = this.getGroupId(group);
      return this.collapsedGroups[groupId] || false; 
    },
    getGroupId(group) {
      if (group.isUnavailable) return 'structure-unavailable';
      if (!group.structure) return 'no-structure';
      return `structure-${group.structure.id}`;
    },
    toggleFocusInput() {
      if (this.showDropdown) {
          this.showDropdown = false;
          this.$refs.input.blur();
      } else {
          this.$refs.input.focus();
          this.showDropdown = true;
      }
    },
    handleBlur() {
      setTimeout(() => {
        // If focus has returned to the input (e.g., from the watcher or another interaction), do not close
        if (document.activeElement === this.$refs.input) {
          return;
        }
        if (!this.isInfoModalOpen) {
           this.showDropdown = false;
        }
      }, 150);
    },
    updateDropdownHeight() {
      this.$nextTick(() => {
        if (!this.$refs.input) return;
        const modalContent = this.$el?.closest?.('.modal-content');
        const inputRect = this.$refs.input.getBoundingClientRect();
        const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
        const spaceBelowToViewport = Math.max(150, Math.floor(viewportHeight - inputRect.bottom - 25));
        const canExit = this.canExitModal || !!this.$el?.closest?.('.interdisciplinary-mock-modal');

        if (modalContent && !canExit) {
          const modalRect = modalContent.getBoundingClientRect();
          const footer = modalContent.querySelector('.modal-footer');
          const footerOffset = footer ? (footer.offsetHeight + 15) : 65;
          const spaceBelowInModal = Math.floor(modalRect.bottom - inputRect.bottom - footerOffset);
          
          if (spaceBelowInModal > 320) {
            this.dropdownMaxHeight = spaceBelowInModal;
          } else {
            this.dropdownMaxHeight = Math.min(480, spaceBelowToViewport);
          }
        } else {
          this.dropdownMaxHeight = Math.min(480, spaceBelowToViewport);
        }
      });
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateDropdownHeight);
    const modalContent = this.$el?.closest?.('.modal-content');
    if (modalContent && window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.showDropdown) {
          this.updateDropdownHeight();
        }
      });
      this.resizeObserver.observe(modalContent);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateDropdownHeight);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
};
</script>

<style scoped>
.input-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.suggestion-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0 0 6px 6px;
  background-color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
  z-index: 10005;
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
  font-size: 90%;
}

.suggestion-dropdown::-webkit-scrollbar {
  width: 6px;
}

.suggestion-dropdown::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 3px;
}

.suggestion-dropdown::-webkit-scrollbar-track {
  background-color: transparent;
}

.suggestion-dropdown li {
  display: flex;
  justify-content: space-between;
  padding: 5px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f1f1f1;
}

.structure-header {
  background-color: #fff8f2;
  color: #e65100;
  font-weight: 600;
  padding: 10px 16px !important;
  cursor: pointer !important;
  border-bottom: 1px solid #f0f0f0 !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  user-select: none;
}

.structure-header:hover {
  background-color: #ffebd8;  
}

.subgroup-header {
  padding-left: 25px !important;
  background-color: #fff8f2;
  color: #e65100;
  font-weight: 500;
  border-top: 1px solid #f0f0f0;
}

.subgroup-header:hover {
  background-color: #ffebd8;
}

.header-text {
  flex-grow: 1;
}

.expand-icon {
  color: #e65100;
  font-size: 20px;
  margin-left: 8px;
  transition: transform 0.2s ease;
  pointer-events: none; 
}

.suggestion-dropdown li.inactive {
  color: #ccc;
  cursor: inherit;
  background-color: #f9f9f9 !important;
}

.suggestion-dropdown li:not(.inactive):hover {
  background-color: #f1f1f1 !important; 
}

.inactive .list-detail {
  color: inherit;
}

.list-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  flex-shrink: 0; 
}

.list-detail:last-child {
  padding-right: 10px;
}

.list-detail {
  color: #333333;
  font-size: 80%;
  text-align: right;
  padding-top: 2px;
}

.list-detail-lang,
.list-detail-ects {
  width: 50px;
}

.list-detail-num {
  white-space: nowrap;
  color: #333;
}

.module-name-bar {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  flex: 1;
}

.suggestion-dropdown .module-name-bar {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.suggestion-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.wide-input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 30px 10px 10px; /* right padding for expand_more icon */
  border: 1px solid #bbb;
  border-radius: 4px;
}

.icon-expand {
  position: absolute;
  right: 0;
  top: 0;
  padding: 10px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.icon-expand.dropdown-shown {
  transform: rotate(180deg);
}

.info-icon {
  font-size: 20px;
  color: #666;
  cursor: pointer;
}

.info-icon:hover {
  color: #333;
}
</style>
